import { useEffect } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { AspectRatioIcon } from './AspectRatioIcon';
import {
  VideoSettings,
  RUNWAY_ASPECT_RATIOS,
  RUNWAY_TEXT_TO_VIDEO_ASPECT_RATIOS,
  RunwayAspectRatio,
  DURATIONS,
} from '../VideoGenerationOptions.types';
import { useTranslation } from 'react-i18next';

const DEFAULT_RUNWAY_ASPECT_RATIO: RunwayAspectRatio = '1280:720';

export type RunwayVideoOptions = Extract<
  VideoSettings,
  {
    model: 'runway-gen-4' | 'runway-gen-4.5';
  }
>;

interface RunwayOptionsProps {
  value: RunwayVideoOptions;
  onChange: (value: VideoSettings) => void;
  hasVideos: boolean;
  disabled?: boolean;
}

/**
 * Renders the chat options for Runway video generation (monks-ui ToggleGroup layout).
 */
export const RunwayGenerationOptions = ({
  value,
  onChange,
  hasVideos,
  disabled,
}: RunwayOptionsProps) => {
  const { t } = useTranslation('chats');

  // Without a start frame this is a text-to-video request, which only supports
  // the landscape/portrait ratios; with a start frame (image-to-video) all
  // ratios are available.
  const hasStartFrame = value.startFrame != null;
  const availableAspectRatios = hasStartFrame
    ? RUNWAY_ASPECT_RATIOS
    : RUNWAY_TEXT_TO_VIDEO_ASPECT_RATIOS;

  // If the start frame is removed while a now-unavailable ratio is selected,
  // reset to a supported default so the selection stays valid.
  useEffect(() => {
    const isAvailable = availableAspectRatios.some((r) => r.value === value.aspectRatio);
    if (!isAvailable) {
      onChange({ ...value, aspectRatio: DEFAULT_RUNWAY_ASPECT_RATIO });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStartFrame]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.runwayOptions.aspectRatio')}
        </span>
        <ToggleGroup
          type="single"
          value={value.aspectRatio}
          onValueChange={(next) => {
            if (next) onChange({ ...value, aspectRatio: next as RunwayAspectRatio });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-2 w-full"
          disabled={disabled}
        >
          {availableAspectRatios.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className="flex flex-col gap-1 h-auto py-2"
            >
              <AspectRatioIcon orientation={option.orientation} />
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {!hasVideos && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            {t('chats.runwayOptions.duration')}
          </span>
          <ToggleGroup
            type="single"
            value={String(value.durationSeconds)}
            onValueChange={(next) => {
              if (next) onChange({ ...value, durationSeconds: Number(next) });
            }}
            variant="outline"
            size="sm"
            className="grid grid-cols-3 w-full"
            disabled={disabled}
          >
            {DURATIONS.map((option) => (
              <ToggleGroupItem key={option.value} value={String(option.value)} className="py-2">
                <span className="text-xs">{option.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}
    </>
  );
};
